import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Phone, Search, MoreVertical,
  CheckCheck, Check, Clock, AlertCircle, Image, Paperclip,
  ArrowLeft, RefreshCw, Settings, User, Building,
  Mic, AlertTriangle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Small chip rendered above the body of audio messages. Shows:
 *   - `pending` / `running`  → spinner + "Transcribing…"
 *   - `failed`               → red note + manual retry button
 *   - `skipped`              → grey note (audio too big or missing URL)
 *   - `completed` w/ transcript → nothing here (the transcript is
 *                                  already in msg.body via the
 *                                  service's `[Voice] …` rewrite)
 *   - `completed` w/ empty transcript → "silent audio" note
 *   - no field at all (legacy row) → "Tap to transcribe" retry button
 */
const VoiceTranscriptStrip = ({ status, transcript, error, mediaUrl }) => {
  const isInflight = status === 'pending' || status === 'running' || (!status && mediaUrl);
  const failed = status === 'failed';
  const skipped = status === 'skipped';
  const completedEmpty = status === 'completed' && (!transcript || transcript.trim() === '');

  // Nothing useful to render — the transcript already in body covers it.
  if (status === 'completed' && transcript && transcript.trim()) return null;

  return (
    <div className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300">
      <Mic size={11} className="text-violet-500" />
      {isInflight && (
        <>
          <RefreshCw size={10} className="animate-spin text-violet-500" />
          <span>Transcribing voice note…</span>
        </>
      )}
      {failed && (
        <>
          <AlertTriangle size={10} className="text-red-500" />
          <span className="text-red-600">Transcription failed{error ? ` — ${error}` : ''}.</span>
        </>
      )}
      {skipped && (
        <>
          <span className="text-slate-500">Transcription skipped{error ? ` (${error})` : ''}.</span>
        </>
      )}
      {completedEmpty && (
        <span className="text-slate-500 italic">Silent / undetectable audio.</span>
      )}
    </div>
  );
};

const WhatsAppInbox = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef(null);
  
  const [conversations, setConversations] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isConfigured, setIsConfigured] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [newChatMode, setNewChatMode] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState('');

  useEffect(() => {
    if (token) {
      checkConfiguration();
      fetchConversations();
    }
  }, [token]);

  // Handle URL params for starting new chat from contacts page
  useEffect(() => {
    const contactId = searchParams.get('contact');
    const phone = searchParams.get('phone');
    
    if (contactId && phone && isConfigured) {
      // Start new chat mode with this contact
      setNewChatMode(true);
      setNewChatPhone(decodeURIComponent(phone));
      setSelectedContact({
        contact_id: contactId,
        contact: { phone: decodeURIComponent(phone) },
        isNewChat: true
      });
    }
  }, [searchParams, isConfigured]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-refresh conversations every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (token && !sending) {
        fetchConversations(true);
        if (selectedContact && !selectedContact.isNewChat) {
          fetchMessages(selectedContact.contact_id, true);
        }
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [token, selectedContact, sending]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const checkConfiguration = async () => {
    try {
      const response = await axios.get(`${API}/twilio-whatsapp/config?token=${token}`);
      setIsConfigured(response.data.configured);
    } catch (error) {
      console.error('Failed to check config:', error);
    }
  };

  const fetchConversations = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await axios.get(`${API}/twilio-whatsapp/conversations?token=${token}`);
      if (response.data.success) {
        setConversations(response.data.conversations || []);
      }
    } catch (error) {
      if (!silent) {
        console.error('Failed to fetch conversations:', error);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchMessages = async (contactId, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await axios.get(
        `${API}/twilio-whatsapp/conversation/${contactId}?token=${token}&limit=100`
      );
      if (response.data.success) {
        setMessages(response.data.messages || []);
      }
    } catch (error) {
      if (!silent) {
        console.error('Failed to fetch messages:', error);
        toast.error('Failed to load messages');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedContact(conv);
    fetchMessages(conv.contact_id);
    
    // Mark as read
    axios.post(
      `${API}/twilio-whatsapp/conversation/${conv.contact_id}/mark-read?token=${token}`
    ).catch(console.error);
    
    // Update unread count locally
    setConversations(prev => prev.map(c => 
      c.contact_id === conv.contact_id ? { ...c, unread_count: 0 } : c
    ));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedContact) return;
    
    const phone = selectedContact.contact?.phone || selectedContact.contact?.whatsapp_number;
    if (!phone) {
      toast.error('No phone number for this contact');
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/twilio-whatsapp/send?token=${token}`, {
        to_phone: phone,
        message: newMessage,
        contact_id: selectedContact.contact_id
      });
      
      if (response.data.success) {
        setNewMessage('');
        // Add message to UI immediately
        setMessages(prev => [...prev, {
          id: response.data.message_id,
          body: newMessage,
          direction: 'outbound',
          status: response.data.status,
          created_at: new Date().toISOString()
        }]);
        toast.success('Message sent!');
        
        // If this was a new chat, refresh conversations to show it
        if (selectedContact.isNewChat) {
          setNewChatMode(false);
          fetchConversations();
          // Clear URL params
          navigate('/whatsapp', { replace: true });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchConversations();
    if (selectedContact && !selectedContact.isNewChat) {
      await fetchMessages(selectedContact.contact_id);
    }
    setRefreshing(false);
    toast.success('Refreshed');
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'read':
        return <CheckCheck size={14} className="text-blue-500" />;
      case 'delivered':
        return <CheckCheck size={14} className="text-gray-400" />;
      case 'sent':
        return <Check size={14} className="text-gray-400" />;
      case 'queued':
      case 'sending':
        return <Clock size={14} className="text-gray-400" />;
      case 'failed':
      case 'undelivered':
        return <AlertCircle size={14} className="text-red-500" />;
      default:
        return <Check size={14} className="text-gray-400" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { timeZone: 'Asia/Dubai', weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { timeZone: 'Asia/Dubai', month: 'short', day: 'numeric' });
    }
  };

  const getInitials = (contact) => {
    const name = contact?.name || contact?.email || 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const contact = conv.contact || {};
    const searchLower = searchQuery.toLowerCase();
    return (
      contact.name?.toLowerCase().includes(searchLower) ||
      contact.phone?.includes(searchQuery) ||
      contact.email?.toLowerCase().includes(searchLower)
    );
  });

  // Not configured state
  if (!isConfigured) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-100px)]" data-testid="whatsapp-not-configured">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto">
              <MessageSquare className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold">WhatsApp Not Configured</h2>
            <p className="text-muted-foreground">
              Set up your Twilio WhatsApp integration to start messaging customers.
            </p>
            <Button onClick={() => navigate('/whatsapp-settings')} className="bg-green-500 hover:bg-green-600">
              <Settings size={16} className="mr-2" />
              Configure WhatsApp
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-80px)]" data-testid="whatsapp-inbox">
      {/* Conversations List */}
      <div className={`w-full md:w-96 border-r flex flex-col ${selectedContact ? 'hidden md:flex' : 'flex'}`}>
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="text-green-500" size={24} />
            <h2 className="font-semibold text-lg">WhatsApp</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/whatsapp-settings')}>
              <Settings size={18} />
            </Button>
          </div>
        </div>
        
        {/* Search */}
        <div className="p-3 border-b">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        
        {/* Conversations */}
        <ScrollArea className="flex-1">
          {loading && conversations.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw size={24} className="animate-spin text-muted-foreground" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare size={48} className="mx-auto mb-2 opacity-50" />
              <p>No conversations yet</p>
              <p className="text-sm">Messages will appear here</p>
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <div
                key={conv.contact_id}
                onClick={() => handleSelectConversation(conv)}
                className={`p-3 border-b cursor-pointer hover:bg-muted/50 transition-colors ${
                  selectedContact?.contact_id === conv.contact_id ? 'bg-muted' : ''
                }`}
                data-testid={`conversation-${conv.contact_id}`}
              >
                <div className="flex items-start gap-3">
                  <Avatar className="w-12 h-12">
                    <AvatarFallback className="bg-green-500/10 text-green-600">
                      {getInitials(conv.contact)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate">
                        {conv.contact?.name || conv.contact?.phone || 'Unknown'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatTime(conv.last_message?.created_at)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-sm text-muted-foreground truncate pr-2">
                        {conv.last_message?.direction === 'outbound' && (
                          <span className="mr-1">{getStatusIcon(conv.last_message?.status)}</span>
                        )}
                        {conv.last_message?.body || 'No messages'}
                      </p>
                      {conv.unread_count > 0 && (
                        <Badge className="bg-green-500 text-white rounded-full min-w-[20px] h-5 flex items-center justify-center">
                          {conv.unread_count}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col ${!selectedContact ? 'hidden md:flex' : 'flex'}`}>
        {selectedContact ? (
          <>
            {/* Chat Header */}
            <div className="p-3 border-b flex items-center gap-3 bg-card">
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden"
                onClick={() => setSelectedContact(null)}
              >
                <ArrowLeft size={20} />
              </Button>
              <Avatar className="w-10 h-10">
                <AvatarFallback className="bg-green-500/10 text-green-600">
                  {getInitials(selectedContact.contact)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-medium">
                  {selectedContact.contact?.name || selectedContact.contact?.phone || 'Unknown'}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {selectedContact.contact?.phone}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => {
                if (selectedContact.contact_id) {
                  navigate(`/contacts?id=${selectedContact.contact_id}`);
                }
              }}>
                <User size={18} />
              </Button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4 bg-[#e5ddd5] dark:bg-gray-900">
              <div className="space-y-2 max-w-3xl mx-auto">
                {messages.map((msg, index) => (
                  <div
                    key={msg.id || index}
                    className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-3 shadow-sm ${
                        msg.direction === 'outbound'
                          ? 'bg-[#dcf8c6] dark:bg-green-800 text-gray-800 dark:text-white rounded-br-none'
                          : 'bg-white dark:bg-gray-800 rounded-bl-none'
                      }`}
                    >
                      {msg.media_urls?.length > 0 && (
                        <div className="mb-2">
                          {msg.media_urls.map((url, i) => (
                            <img
                              key={i}
                              src={url}
                              alt="Media"
                              className="max-w-full rounded"
                            />
                          ))}
                        </div>
                      )}
                      {/* Voice-note transcript rendering. When the
                          backend has transcribed the audio, `msg.body`
                          already holds the "[Voice] <text>" form
                          (rewritten in voice_transcription_service).
                          For in-flight transcriptions we show a
                          pending indicator; for failures we show a
                          retry hint. */}
                      {(msg.message_type === 'audioMessage' || msg.media_type === 'audio') && (
                        <VoiceTranscriptStrip
                          status={msg.transcript_status}
                          transcript={msg.transcript}
                          error={msg.transcript_error}
                          mediaUrl={msg.media_url}
                        />
                      )}
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.body}</p>
                      <div className="flex items-center justify-end gap-1 mt-1">
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">
                          {new Date(msg.created_at).toLocaleTimeString('en-US', { 
                            timeZone: 'Asia/Dubai',
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                        {msg.direction === 'outbound' && getStatusIcon(msg.status)}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-3 border-t bg-card flex gap-2">
              <Button type="button" variant="ghost" size="icon" className="shrink-0">
                <Paperclip size={20} />
              </Button>
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1"
                disabled={sending}
                data-testid="message-input"
              />
              <Button 
                type="submit" 
                disabled={!newMessage.trim() || sending}
                className="bg-green-500 hover:bg-green-600 shrink-0"
                data-testid="send-message-btn"
              >
                <Send size={18} />
              </Button>
            </form>
          </>
        ) : (
          // No conversation selected
          <div className="flex-1 flex items-center justify-center bg-muted/30">
            <div className="text-center">
              <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare size={48} className="text-muted-foreground" />
              </div>
              <h3 className="text-xl font-medium text-muted-foreground">WhatsApp Web</h3>
              <p className="text-muted-foreground mt-2">
                Select a conversation to start messaging
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppInbox;
